import { ErrorHandler, Injectable, inject, provideAppInitializer } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { sanitizeTelemetry, telemetryUrl } from './sanitize';

declare const SENTRY_RELEASE: string;

export const SENTRY_DSN = 'https://6c1dc8e78a11b3c06657d3fe86cae92e@sentry.ancelade.com/30';
export const APPLICATION = 'vitrine';
export const TRACE_TARGETS = [
  /^https:\/\/api(?:\.p)?\.ancelade\.cloud(?:\/|$)/,
  /^https:\/\/ancelade-backend-api\.preview\.moncompanion\.cloud(?:\/|$)/,
  /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/,
];

export function sentryEnvironment(hostname: string): string {
  if (hostname.endsWith('.preview.moncompanion.cloud')) return 'preview';
  if (hostname.includes('.p.ancelade.') || hostname.startsWith('preprod.')) return 'preproduction';
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) return 'development';
  return 'production';
}

export function initSentry(): void {
  if (typeof window === 'undefined' || Sentry.getClient()) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: sentryEnvironment(window.location.hostname),
    release: 'ancelade-vitrine@' + (typeof SENTRY_RELEASE === 'undefined' ? 'development' : SENTRY_RELEASE), // NOSONAR: SENTRY_RELEASE is only defined by scripts/build.mjs (--define); a direct comparison would throw a ReferenceError under ng serve/test
    sampleRate: 1,
    tracesSampleRate: 1,
    // Classic transactions work with self-hosted Sentry as well as current cloud releases.
    traceLifecycle: 'static',
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
      databaseQueryData: false,
      queues: false,
    },
    attachStacktrace: true,
    maxBreadcrumbs: 100,
    normalizeDepth: 8,
    tracePropagationTargets: TRACE_TARGETS,
    initialScope: { tags: { application: APPLICATION } },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['debug', 'info', 'log', 'warn', 'error', 'assert'] }),
      Sentry.extraErrorDataIntegration({ depth: 5 }),
    ],
    beforeSend: (event) => {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.query_string;
      }
      return sanitizeTelemetry(event);
    },
    beforeSendTransaction: (event) => sanitizeTelemetry(event),
    beforeSendLog: (log) => sanitizeTelemetry(log),
    beforeBreadcrumb: (breadcrumb) => sanitizeTelemetry(breadcrumb),
  });
  void import('./replay').then(({ enableReplay }) => enableReplay()).catch(captureBootstrapError);
}

const capturedHttpErrors = new WeakSet<HttpErrorResponse>();

/** One issue per endpoint, not per resource identifier. */
export function telemetryEndpoint(url: string): string {
  return url.replace(/\/(?:\d+|(?=[^/]*\d)[\w-]{8,})(?=\/|$)/g, '/{id}');
}

/** Problem details the API returned to this client, never other body members. */
function problemDetails(error: HttpErrorResponse): Record<string, string> {
  const body: unknown = error.error;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
  const problem: Record<string, string> = {};
  for (const key of ['type', 'title', 'detail', 'message', 'correlation_id']) {
    const value = (body as Record<string, unknown>)[key];
    if (typeof value === 'string' && value !== '') problem[key] = value.slice(0, 500);
  }
  return problem;
}

export function captureHttpFailure(error: HttpErrorResponse, method = 'HTTP', url = error.url ?? ''): void {
  if (capturedHttpErrors.has(error)) return;
  capturedHttpErrors.add(error);
  const safeUrl = telemetryUrl(url);
  const endpoint = telemetryEndpoint(safeUrl);
  const problem = problemDetails(error);
  const explanation = problem['detail'] ?? problem['title'] ?? problem['message'];
  const suffix = explanation ? ' — ' + explanation : '';
  const failure = new Error(`HTTP ${error.status} ${method} ${endpoint}${suffix}`);
  failure.name = 'HttpRequestError';
  Sentry.captureException(failure, {
    level: error.status === 0 || error.status >= 500 ? 'error' : 'warning',
    fingerprint: ['http', method, String(error.status), endpoint],
    tags: { 'http.status_code': error.status, 'http.method': method },
    contexts: {
      http: {
        url: safeUrl,
        status_code: error.status,
        method,
        // The API also returns its correlation identifier in problem details.
        request_id: error.headers.get('X-Request-ID') ?? problem['correlation_id'] ?? null,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      },
      ...(Object.keys(problem).length > 0 ? { problem } : {}),
    },
  });
}

export const sentryHttpInterceptor: HttpInterceptorFn = (request, next) =>
  next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) captureHttpFailure(error, request.method, request.url);
      return throwError(() => error);
    })
  );

@Injectable()
export class TelemetryErrorHandler implements ErrorHandler {
  private readonly sentry = Sentry.createErrorHandler({ logErrors: false });

  handleError(error: unknown): void {
    // Static prerendering has no browser SDK: preserve Angular's server diagnostics.
    if (typeof window === 'undefined') {
      new ErrorHandler().handleError(error);
      return;
    }
    const unwrapped = error && typeof error === 'object' && 'rejection' in error ? error.rejection : error;
    if (unwrapped instanceof HttpErrorResponse) {
      captureHttpFailure(unwrapped);
      return;
    }
    this.sentry.handleError(error);
  }
}

export function provideSentry() {
  return [
    { provide: ErrorHandler, useClass: TelemetryErrorHandler },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ];
}

export function captureBootstrapError(error: unknown): void {
  Sentry.captureException(error, { tags: { phase: 'bootstrap' } });
  // The console log is a sanitized diagnostic, separately searchable in Sentry Logs.
  console.error('Application bootstrap failed', sanitizeTelemetry(String(error)));
}

export function setTelemetryUser(id: string | number | null, role: string): void {
  Sentry.setUser(id === null ? null : { id: `${role}:${id}` });
}
